import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { useAuth } from '../../hooks/useAuth';
import { isAdmin } from '../../lib/roles';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { ServicioListItem } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

export function ServiciosList() {
  const { perfil } = useAuth();
  // Editar/crear servicios es exclusivo de admin -- confirmado contra
  // Postgres real: la política `servicios_admin_write` de schema.sql
  // exige `is_admin()`, no `is_staff()` (DAF v2 §5.1 "Configurar
  // servicios, precios y profesionales" está listado solo para
  // Administrador, no para Recepción). Antes de este ajuste, tanto
  // recepción como profesional veían "Nuevo servicio"/"Editar" aunque
  // RLS ya les rechazaba el guardado en silencio (0 filas afectadas,
  // sin error) -- este cambio corrige la UI para que coincida con lo
  // que el servidor de verdad permite, no solo lo pedido por el usuario
  // para profesional.
  const admin = isAdmin(perfil?.rol);

  const { data, loading, error } = useSupabaseQuery<ServicioListItem[]>(async () => {
    const { data, error } = await supabase
      .from('servicios')
      .select('*, categorias_servicios ( nombre )')
      .neq('estado', 'anulado')
      .order('nombre', { ascending: true });

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      ...(row as unknown as ServicioListItem),
      categoria_nombre:
        (row.categorias_servicios as { nombre: string } | null)?.nombre ?? 'Sin categoría',
    }));

    return { data: items, error: null };
  });

  return (
    <div>
      <PageHeader
        title="Servicios"
        description="Catálogo de servicios por categoría — Uñas, Pestañas, Cabello y las que agregues"
        actions={
          admin ? (
            <Link to="/admin/servicios/nuevo" className="btn-primary">
              Nuevo servicio
            </Link>
          ) : undefined
        }
      />

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && (
        <EmptyState title="No hay servicios registrados" description="Crea el primero desde el botón de arriba." />
      )}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Servicio</th>
                <th className="px-4 py-3 font-medium">Categoría</th>
                <th className="px-4 py-3 font-medium">Duración</th>
                <th className="px-4 py-3 font-medium">Precio</th>
                <th className="px-4 py-3 font-medium">Reserva online</th>
                {admin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <tr key={s.id} className="border-b border-onyx/5 last:border-0 hover:bg-onyx/[0.02]">
                  <td className="px-4 py-3 font-medium text-onyx">{s.nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{s.categoria_nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{s.duracion_minutos} min</td>
                  <td className="px-4 py-3 text-onyx/70">{COP.format(s.precio)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`badge ${
                        s.disponible_online ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {s.disponible_online ? 'Sí' : 'No'}
                    </span>
                  </td>
                  {admin && (
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/servicios/${s.id}`} className="text-gold-deep hover:underline">
                        Editar
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
